import {
  Link,
  Outlet,
  createFileRoute,
  notFound,
} from '@tanstack/react-router';
import { LockIcon } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { z } from 'zod';

import { AppShell } from '@/features/app-shell';
import { TaskDraftDock } from '@/features/tasks';
import { clearPersonalDrafts } from '@/shared/store/personalDrafts';
import { FeatureErrorBoundary } from '@/shared/components/FeatureErrorBoundary';
import { WorkspacePendingBody } from '@/features/app-shell';
import {
  RequestAccess,
  StandingPill,
  useWorkspaceRefusal,
  useWorkspaceStanding,
} from '@/features/workspaces';
import { ACCESS_COPY } from '@/features/workspaces/accessCopy';
import { CanWorkContext } from '@/features/workspaces/canWork';
import { Button, buttonVariants } from '@/components/ui/button';
import { useSession } from '@/lib/auth/session';
import { AccountMenu } from '@/shared/components/AccountMenu';
import {
  BandPresenceContext,
  BandStandingContext,
} from '@/shared/components/bandStanding';
import { BrandMark } from '@/shared/components/BrandMark';
import { NotFoundState } from '@/shared/components/NotFoundState';
import { usePreferencesStore } from '@/shared/store/preferences';

const workspaceIdSchema = z.uuid();

export const Route = createFileRoute('/workspaces/$workspaceId')({
  /**
   * A workspace identifier is a UUID. A malformed one names no resource, so it
   * is a not-found condition and can be rejected without loading anything.
   *
   * A well-formed identifier for a workspace that does not exist is also
   * not-found, but only the workspace read can tell; the layout below shows the
   * same surface once that read says so (spec 188).
   */
  beforeLoad: ({ params }) => {
    if (!workspaceIdSchema.safeParse(params.workspaceId).success) {
      // TanStack Router signals not-found by throwing its own marker object,
      // not an Error. This is the framework's documented control flow.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw notFound();
    }
  },
  component: WorkspaceLayoutRoute,
  notFoundComponent: WorkspaceNotFound,
  pendingComponent: WorkspacePending,
});

export function WorkspaceLayoutRoute() {
  const { workspaceId } = Route.useParams();
  // Where this person was, for the front door's "pick up where you left off"
  // row. Recorded here because this is the one component every page of a
  // workspace renders under; a page-level record would miss deep links.
  const rememberWorkspace = usePreferencesStore(
    (state) => state.rememberWorkspace,
  );
  // Reads the workspace every page renders from — here too, so its refusal is known
  // before any page decides to say "could not be loaded" (spec 188).
  const standing = useWorkspaceStanding(workspaceId);
  const refusal = useWorkspaceRefusal(workspaceId);
  useEffect(() => {
    // Only a workspace this person can reach is somewhere they "left off".
    if (refusal === null) rememberWorkspace(workspaceId);
  }, [workspaceId, rememberWorkspace, refusal]);
  // Only for someone the pill speaks to: a member's band gets nothing, so it draws
  // no empty actions row. One element per standing, not one per render, so the
  // bands are not re-rendered by the layout's own ticks.
  const speaks = standing.readOnly || standing.asOrgAdmin;
  const pill = useMemo(
    () => (speaks ? <StandingPill workspaceId={workspaceId} /> : null),
    [speaks, workspaceId],
  );
  // How many bands on screen are saying it: while one is, the top bar does not.
  const [bands, setBands] = useState(0);
  const register = useCallback(() => {
    setBands((count) => count + 1);
    return () => setBands((count) => count - 1);
  }, []);
  const presence = useMemo(
    () => ({ register, shown: bands > 0 }),
    [register, bands],
  );
  // Neither one is given the shell: every link in its sidebar would lead back here.
  if (refusal === 'not_accessible') return <WorkspaceNoAccess />;
  if (refusal === 'not_found') return <WorkspaceNotFound />;
  return (
    // Every control below asks this before offering a change (spec 189).
    <CanWorkContext.Provider value={!standing.readOnly}>
      {/* Every page's band says where a non-member stands (spec 196 US1). */}
      <BandStandingContext.Provider value={pill}>
        <BandPresenceContext.Provider value={presence}>
          <AppShell workspaceId={workspaceId}>
            <FeatureErrorBoundary>
              <Outlet />
            </FeatureErrorBoundary>
            {/*
        Task drafts outlive any one page of the workspace (spec 121): a dialog
        folded away on a board can be finished from anywhere in it.
      */}
            {standing.readOnly ? null : (
              <TaskDraftDock workspaceId={workspaceId} />
            )}
          </AppShell>
        </BandPresenceContext.Provider>
      </BandStandingContext.Provider>
    </CanWorkContext.Provider>
  );
}

export function WorkspacePending() {
  /*
   * The shell stays; only the outlet waits (spec 079 FR-005, research D2).
   *
   * This was the most extreme case of the defect that feature exists to remove: the
   * router's pending component replaced `WorkspaceLayoutRoute` outright, so a cold
   * workspace load blanked the entire application — sidebar, topbar and all — and then
   * rebuilt it. Nothing required that. `AppShell` takes only `workspaceId`, which is a
   * route param and therefore known before any request is made, and `Sidebar` already
   * tolerates the pending case on purpose: it withholds its badge rather than showing a
   * placeholder number, and defaults to not-owner while identity loads.
   *
   * Deliberately *not* applied to `WorkspaceNotFound` below. A workspace that does not
   * exist must not be given a sidebar whose every link points back into it — pending and
   * not-found are different conditions and only one of them has a shell to draw.
   */
  const { workspaceId } = Route.useParams();
  return (
    <AppShell workspaceId={workspaceId}>
      <WorkspacePendingBody />
    </AppShell>
  );
}

/**
 * A Private workspace this person is not in (spec 188, decision Q15).
 *
 * Says neither its name nor its owners: the service answers this read with only
 * "not accessible", and a page that named the workspace would tell anyone holding a
 * link what it is. Without the shell for the same reason as not-found below.
 *
 * A hero on the inverse surface since spec 196 (US2): it was a lock and two lines on
 * an empty page, which read as an error rather than a door. The brand grid applies
 * on a hero surface (FD-17), so the content sits on the 12-column grid. The full
 * logo, not the spade: the spade alone may appear only where the full logo already
 * has (FD-18), and this page has no shell to have shown it.
 */
export function WorkspaceNoAccess() {
  const { workspaceId } = Route.useParams();
  const { signOut, switchAccount } = useSession();
  return (
    <OutsideTheShell>
      <section
        data-surface="inverse"
        className="dark bg-surface-inverse text-on-dark grid flex-1 grid-cols-12 content-center gap-x-6 px-6 py-16 sm:px-10"
      >
        <div className="col-span-12 flex flex-col items-start gap-6 md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4">
          <BrandMark className="text-on-dark" />
          {/*
            48px, above the icon scale's empty-state step (FD-15a, size-6): this is
            the page's one image, on a hero surface, where 24px read as a stray
            glyph beside a 3xl title. Line art at the scale's one stroke.
          */}
          <LockIcon
            aria-hidden="true"
            strokeWidth={1.5}
            className="text-accent-line size-12"
          />
          <div className="flex flex-col gap-2">
            <h1 className="text-on-dark text-3xl font-light tracking-tight">
              {ACCESS_COPY.noAccessTitle}
            </h1>
            <p className="text-on-dark/80 max-w-prose text-sm leading-relaxed">
              {ACCESS_COPY.noAccessBody}
            </p>
          </div>
          {/* The one thing to do here, then the ways out (spec 190, spec 196). */}
          <div className="flex flex-col items-start gap-4">
            <RequestAccess workspaceId={workspaceId} holdPlace />
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/" className={buttonVariants({ variant: 'outline' })}>
                {ACCESS_COPY.backToWorkspaces}
              </Link>
              <Button
                variant="ghost"
                onClick={() => {
                  // Another person follows: this one's drafts go, as they do
                  // from the account menu (spec 197).
                  clearPersonalDrafts();
                  (switchAccount ?? signOut)();
                }}
              >
                {ACCESS_COPY.switchAccount}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </OutsideTheShell>
  );
}

/**
 * Rendered without the shell on purpose: the sidebar is workspace-scoped, and
 * every link in it would point back into a workspace that does not exist.
 */
export function WorkspaceNotFound() {
  return (
    <OutsideTheShell>
      <NotFoundState
        title="Workspace not found"
        description="This workspace does not exist, or it is no longer available to you. It may have been removed since the link was shared."
        action={
          // Foreground and underlined, not Light Blue: small text on the dark
          // theme's surfaces fails as Light Blue (WD-04).
          <Link
            to="/"
            className="text-foreground focus-visible:ring-ring rounded-sm text-sm underline underline-offset-2 hover:text-foreground/80 focus-visible:ring-3 focus-visible:outline-none"
          >
            Back to all workspaces
          </Link>
        }
      />
    </OutsideTheShell>
  );
}

/**
 * The account menu, and nothing else, above a page that has no shell (spec 193).
 *
 * Someone signed in with the wrong account reaches these pages more than anyone,
 * and without the shell they had no way to see who they were or to sign out: the
 * account lives in the sidebar, which these pages are drawn without on purpose.
 */
export function OutsideTheShell({ children }: { children: ReactNode }) {
  return (
    <div className="bg-background flex min-h-screen flex-col">
      <header className="flex shrink-0 items-center justify-end px-4 py-2.5 sm:px-8">
        <AccountMenu />
      </header>
      <main className="flex flex-1 flex-col">{children}</main>
    </div>
  );
}
